import { Fragment, useState, useEffect, useRef, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { AddressSearchField } from "./AddressSearchField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  X,
  Plus,
  User,
  Heart,
  Pill,
  FileText,
  Stethoscope,
  UserPlus,
  Mail,
  Phone,
  MapPin,
  Edit2,
  Trash2,
  AlertTriangle,
  ArrowRight,
  Search,
  CheckCircle2,
  Upload,
} from "lucide-react";
import { UploadDocumentModal } from "@/components/PatientDashboardV2/components/UploadDocumentModal";
import { PatientDocument } from "@/lib/dashboard/patientDocumentsData";
import { statusMap, useAddPatientFormData } from "@/lib/patient/addPatientData";
import { ImWarning } from "react-icons/im";
import { AddAllergyButton } from "@/components/PatientDashboardV2/components/PatientProfile/components/AllergyModal";
import { AddConditionButton } from "@/components/PatientDashboardV2/components/PatientProfile/components/ConditionsModal";
import { AddMedicationButton } from "@/components/PatientDashboardV2/components/PatientProfile/components/MedicationsModal";
import SearchableDropdown from "@/components/AddPrescriptionItem/SearchableDropdown";
import DoctorSearchableDropdown from "@/components/PatientDashboardV2/components/PatientProfile/components/DoctorsSearchableDropdown";
import { severityNumberToString } from "@/lib/allergyData";
import { therapyLevelNumberToString } from "@/lib/medicationsData";
import { statusNumberToString } from "@/lib/ConditionsData";
import TagsPicker from "@/components/TagsPicker/TagsPicker";
import { DeleteConfirmationModal } from "@/components/DeleteConfirmationModal";
import { deleteConfirmationMessage } from "@/data/constantStrings";
import {
  searchPDSByName,
  searchPDSByNHS,
  validateNHSNumber,
  formatNHSNumber,
  PDSPatient,
} from "@/services/pdsService";
import {
  searchSurgery,
  getSurgeryById,
  setSurgery,
} from "@/dispatchers/patientDispatchers";

interface AddressSummary {
  addressLineOne: string;
  addressLineTwo: string;
  postTown: string;
  postalCounty: string;
  postCode: string;
}

function AddPatientPopUp({ open, onOpenChange, onSave, isDarkMode, editMode }) {
  const getSurgeryEmailOptions = (surgery: any): string[] => {
    if (!surgery) return [];

    const rawEmails = [
      ...(Array.isArray(surgery?.Email)
        ? surgery.Email
        : surgery?.Email
          ? [surgery.Email]
          : []),
      ...(Array.isArray(surgery?.emailAddress)
        ? surgery.emailAddress
        : surgery?.emailAddress
          ? [surgery.emailAddress]
          : []),
      ...(Array.isArray(surgery?.email)
        ? surgery.email
        : surgery?.email
          ? [surgery.email]
          : []),
    ];

    const cleanedEmails = rawEmails
      .map((email) => String(email || "").trim())
      .filter(Boolean);

    return [...new Set(cleanedEmails)];
  };

  const getSurgeryGpOptions = (surgery: any): any[] => {
    if (!surgery) return [];

    const surgeryGpSource: any =
      surgery?.generalPractitioners ??
      surgery?.GeneralPractitioners ??
      surgery?.generalPractitioner ??
      surgery?.GeneralPractitioner ??
      [];

    return Array.isArray(surgeryGpSource)
      ? surgeryGpSource
      : surgeryGpSource
        ? [surgeryGpSource]
        : [];
  };

  const extractSurgeryFromApiPayload = (payload: any) => {
    if (!payload) return null;
    if (payload?.id || payload?._id) return payload;

    const nested =
      payload?.data ??
      payload?.result ??
      payload?.responseData ??
      payload?.value ??
      payload?.item ??
      null;

    if (nested?.id || nested?._id) return nested;
    return null;
  };

  const [uploadDocumentModalOpen, setUploadDocumentModalOpen] = useState(false);
  const [showInlineAddRelated, setShowInlineAddRelated] = useState(false);
  const [postcodeValue, setPostcodeValue] = useState("");
  const [addresses, setAddresses] = useState<AddressSummary[]>([]);
  const [surgerySearchQuery, setSurgerySearchQuery] = useState("");
  const [selectedSurgery, setSelectedSurgery] = useState(null);
  const [showSurgeryResults, setShowSurgeryResults] = useState(false);
  const [surgerySearchResults, setSurgerySearchResults] = useState([]);
  const [isManualGpEntry, setIsManualGpEntry] = useState(false);
  const [gpSearchQuery, setGpSearchQuery] = useState("");
  const [gpSearchResults, setGpSearchResults] = useState([]);
  const [showGpResults, setShowGpResults] = useState(false);
  const [showEmailResults, setShowEmailResults] = useState(false);

  const {
    formData,
    setFormData,
    activeTab,
    setActiveTab,
    errors,
    updatePatientDetailsLoader,
    addPatientLoader,
    addPatientFormMessage,
    selectedPatient,
    setSelectedPatient,
    selectedRelationshipType,
    setSelectedRelationshipType,
    addRelationship,
    removeRelationship,
    handleInputChangeWrapper,
    handleSubmitWrapper,
    resetFormWrapper,
    titleOptions,
    genderOptions,
    relationshipOptions,
    ethnicityOptions,
    requiresConsent,
    hasGuardian,
    patientAge,
    patientSearchQuery,
    setPatientSearchQuery,
    searchResults,
    isSearching,
    showSearchResults,
    setShowSearchResults,
    getSeverityColorClass,
    getStatusColorClass,
    getTherapyLevelColorClass,
    therapyLevelMap,
    handleSaveAllergyWrapper,
    handleEditAllergyWrapper,
    handleDeleteAllergyWrapper,
    handleSaveConditionWrapper,
    handleEditConditionWrapper,
    handleDeleteConditionWrapper,
    handleSaveMedicationWrapper,
    handleEditMedicationWrapper,
    handleDeleteMedicationWrapper,
    handleDoctorSearch,
    searchedDoctors,
    deleteModalOpen,
    setDeleteModalOpen,
    deleteAction,
    deleteMessage,
    confirmDelete,
    tagsOptions,
    setTagsOptions,
    isCompanyTagsLoading,
    toggleTagsPickerDropdown,
    isOpenTagsPickerDropdown,
    setIsOpenTagsPickerDropdown,
    selectedCompanyTags,
    tagFilterValue,
    setTagFilterValue,
    generateRandomTagsColor,
    previewTagColor,
    setPreviewTagColor,
    handleSelectTag,
    handleAddCompanyTag,
    currentTag,
    setCurrentTag,
    handleDeleteTag,
    editingTag,
    setEditingTag,
    handlePDSNameSearch,
    handlePDSNHSSearch,
    handleSelectPDSPatient,
    handleSkipPDSSearch,
    handleBackToPDSSearch,
    resetPDSState,
    currentStep,
    setPDSSearchMode,
    setPDSSearchError,
    pdsSearchMode,
    pdsGivenName,
    pdsFamilyName,
    pdsGender,
    pdsDOB,
    pdsPostcode,
    isPDSSearching,
    pdsNHSNumber,
    pdsNHSDOB,
    pdsSearchError,
    pdsSearchResults,
    setPDSSearchResults,
    setPDSGivenName,
    setPDSFamilyName,
    setPDSGender,
    setPDSDOB,
    setPDSPostcode,
    setPDSNHSNumber,
    setPDSNHSDOB,
    givenNameLocal,
    setGivenNameLocal,
    familyNameLocal,
    setFamilyNameLocal,
    searchByDemographics,
    searchByNhsNumber,
    companyId,
    currentPharmacyId,
    handleAddNewRelatedPatient,
    searchedSurgeries,
    surgeryData,
  } = useAddPatientFormData({
    open,
    onOpenChange,
    onSave,
    isDarkMode,
    editMode,
  });
  const validPatients = pdsSearchResults.filter(
    (p) => p?.nhsNumber && p.nhsNumber.trim() !== "",
  );
  const handleUploadDocument = (documentData: {
    file: File;
    type: PatientDocument["type"];
    customName: string;
  }) => {
    // This handler can be used to upload documents for the new/edited patient
    // For now, we're just closing the modal
    setUploadDocumentModalOpen(false);
  };

  const surgeryInputRef = useRef<HTMLInputElement>(null);
  const gpInputRef = useRef<HTMLInputElement>(null);
  const lastSelectedSurgeryKeyRef = useRef<string>("");
  const apiSurgeryData = useMemo(
    () => extractSurgeryFromApiPayload(surgeryData),
    [surgeryData],
  );
  const surgerySource = useMemo(() => {
    // If user cleared surgery, do not reuse previously fetched surgeryData from store.
    if (!selectedSurgery && !formData?.surgeryData) {
      return null;
    }

    const base = formData?.surgeryData || {};
    const selected = selectedSurgery || {};
    const api = apiSurgeryData || {};
    const merged = {
      ...base,
      ...selected,
      ...api,
    };

    return Object.keys(merged).length > 0 ? merged : null;
  }, [formData?.surgeryData, selectedSurgery, apiSurgeryData]);
  const surgeryEmailOptions = useMemo(() => {
    const options = getSurgeryEmailOptions(surgerySource);
    if (options.length > 0) return options;

    const currentEmail = String(formData?.surgeryEmail || "").trim();
    return currentEmail ? [currentEmail] : [];
  }, [surgerySource, formData?.surgeryEmail]);
  const surgeryGpOptions = useMemo(() => {
    const options = getSurgeryGpOptions(surgerySource);
    if (options.length > 0) return options;

    if (formData?.gpData) {
      return Array.isArray(formData.gpData)
        ? formData.gpData
        : [formData.gpData];
    }
    return [];
  }, [surgerySource, formData?.gpData]);

  useEffect(() => {
    if (!selectedSurgery) return;
    if (surgeryEmailOptions.length === 0) return;

    const currentSurgeryKey = String(
      selectedSurgery?._id ||
        selectedSurgery?.id ||
        selectedSurgery?.name ||
        "",
    );
    const hasSurgeryChanged =
      lastSelectedSurgeryKeyRef.current !== currentSurgeryKey;

    if (!hasSurgeryChanged) return;

    lastSelectedSurgeryKeyRef.current = currentSurgeryKey;

    setFormData((prev: any) => {
      const currentEmail = prev?.surgeryEmail ?? "";
      if (!currentEmail || !surgeryEmailOptions.includes(currentEmail)) {
        return {
          ...prev,
          surgeryEmail: surgeryEmailOptions[0],
        };
      }
      return prev;
    });
  }, [selectedSurgery, surgeryEmailOptions, setFormData]);

  useEffect(() => {
    if (editMode) {
      // editMode changed - form will reinitialize with new patient data
    }
  }, [editMode]);

  useEffect(() => {
    if (editMode && formData?.surgeryData && !selectedSurgery) {
      setSelectedSurgery(formData.surgeryData);
      setSurgerySearchQuery(formData.surgeryData?.name || "");
      lastSelectedSurgeryKeyRef.current = String(
        formData.surgeryData?._id || formData.surgeryData?.id || "",
      );

      if (formData?.doctorName) {
        setGpSearchQuery(formData.doctorName);
      }
    }
  }, [editMode, formData?.surgeryData, formData?.doctorName]);

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen) {
          resetFormWrapper();
          resetPDSState();
          setUploadDocumentModalOpen(false);
        }
        onOpenChange(newOpen);
      }}
    >
      <DialogContent
        onInteractOutside={(e) => e.preventDefault()}
        className={`add-patient-content max-w-4xl max-h-[90vh] flex flex-col transition-colors duration-300 ${
          activeTab === "surgery" ? "overflow-visible" : "overflow-hidden"
        } ${activeTab === "surgery" ? "max-h-[99vh]" : "max-h-[90vh]"} ${
          isDarkMode
            ? "bg-gray-900 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        <DialogHeader className="flex-shrink-0">
          {!editMode && currentStep === "form-entry" && (
            <div className="px-6 pt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToPDSSearch}
                className="text-blue-600 hover:text-blue-700"
              >
                ← Back to PDS Search
              </Button>
            </div>
          )}

          <DialogTitle
            className={`flex items-center gap-2 transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {editMode ? (
              <>
                <Edit2 className="w-5 h-5" />
                Edit Patient Profile
              </>
            ) : (
              <>
                <UserPlus className="w-5 h-5" />
                Add New Patient
              </>
            )}
          </DialogTitle>
        </DialogHeader>
        {/* PDS Search Step - show before existing form if not editing */}
        {!editMode && currentStep === "pds-search" && (
          <div className="flex-1 p-6 space-y-6 overflow-y-auto min-h-0">
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b">
                <Search className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-semibold">
                  Search Patient Demographic Service (PDS)
                </h3>
              </div>

              <p className="text-sm text-gray-600">
                Search for the patient in the Patient Demographic Service to
                auto-populate their details, or skip to add manually.
              </p>

              <div className="flex gap-2 border-b">
                <button
                  onClick={() => {
                    setPDSSearchMode("name");
                    setPDSSearchError("");
                    setPDSSearchResults([]);
                  }}
                  className={`px-4 py-2 text-sm font-medium border-b-2 ${
                    pdsSearchMode === "name"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Name Search
                </button>
                <button
                  onClick={() => {
                    setPDSSearchMode("nhs");
                    setPDSSearchError("");
                    setPDSSearchResults([]);
                  }}
                  className={`px-4 py-2 text-sm font-medium border-b-2 ${
                    pdsSearchMode === "nhs"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-600 hover:text-gray-900"
                  }`}
                >
                  NHS Number Search
                </button>
              </div>

              {pdsSearchMode === "name" && (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg min-h-[280px]">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="pds-given-name">Given Name *</Label>
                      <Input
                        id="pds-given-name"
                        value={pdsGivenName}
                        onChange={(e) => setPDSGivenName(e.target.value)}
                        placeholder="Enter given name"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="pds-family-name">Family Name *</Label>
                      <Input
                        id="pds-family-name"
                        value={pdsFamilyName}
                        onChange={(e) => setPDSFamilyName(e.target.value)}
                        placeholder="Enter family name"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="pds-gender">Gender *</Label>
                      <select
                        id="pds-gender"
                        value={pdsGender}
                        onChange={(e) => setPDSGender(e.target.value)}
                        className="w-full h-10 px-3 py-2 mt-1 border border-gray-300 rounded-md"
                      >
                        <option value="">Select gender</option>
                        {genderOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="pds-dob">Date of Birth *</Label>
                      <Input
                        id="pds-dob"
                        type="date"
                        value={pdsDOB}
                        onChange={(e) => setPDSDOB(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="pds-postcode">Postcode (Optional)</Label>
                      <Input
                        id="pds-postcode"
                        value={pdsPostcode}
                        onChange={(e) => setPDSPostcode(e.target.value)}
                        placeholder="e.g., BS1 2AB"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={handlePDSNameSearch}
                    disabled={isPDSSearching}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {isPDSSearching ? (
                      <>Searching PDS...</>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Search PDS
                      </>
                    )}
                  </Button>
                </div>
              )}

              {pdsSearchMode === "nhs" && (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg min-h-[280px]">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="pds-nhs-number">NHS Number *</Label>
                      <Input
                        id="pds-nhs-number"
                        value={pdsNHSNumber}
                        onChange={(e) => {
                          const digits = e.target.value
                            .replace(/[^\d]/g, "")
                            .slice(0, 10);
                          const formatted = digits.replace(
                            /(\d{3})(\d{3})(\d{4})/,
                            "$1 $2 $3",
                          );
                          setPDSNHSNumber(formatted.trim());
                        }}
                        placeholder="e.g., 1234567890"
                        className="mt-1"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        10 digits, with or without spaces
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="pds-nhs-dob">Date of Birth *</Label>
                      <Input
                        id="pds-nhs-dob"
                        type="date"
                        value={pdsNHSDOB}
                        onChange={(e) => setPDSNHSDOB(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={handlePDSNHSSearch}
                    disabled={isPDSSearching}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {isPDSSearching ? (
                      <>Searching PDS...</>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Search PDS
                      </>
                    )}
                  </Button>
                </div>
              )}

              {pdsSearchError && (
                <div className="p-4 border border-yellow-200 rounded-lg bg-yellow-50">
                  <div className="flex items-center gap-2 text-yellow-800">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {pdsSearchError}
                    </span>
                  </div>
                </div>
              )}

              {pdsSearchResults?.length > 0 && (
                <div className="space-y-3">
                  {validPatients.length > 0 ? (
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <h4 className="font-medium text-gray-900">
                        {validPatients.length === 1
                          ? "Patient Found"
                          : `${validPatients.length} Patients Found`}
                      </h4>
                    </div>
                  ) : (
                    <div className="p-4 border border-yellow-200 rounded-lg bg-yellow-50">
                      <div className="flex items-center gap-2 text-yellow-800">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          No patient found in PDS. You can add them manually.
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    {pdsSearchResults
                      ?.filter((p) => p?.nhsNumber && p.nhsNumber.trim() !== "")
                      ?.map((patient, idx) => {
                        const formatGender = (sex) => {
                          if (sex === 1) return "Male";
                          if (sex === 2) return "Female";
                          return "Not specified";
                        };
                        const formatDOB = (dob) => {
                          if (!dob) return "";
                          return new Date(dob).toLocaleDateString("en-GB");
                        };
                        return (
                          <div
                            key={idx}
                            className="p-4 bg-white border border-gray-200 rounded-lg cursor-pointer hover:border-blue-300"
                            onClick={() => handleSelectPDSPatient(patient)}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">
                                  {(patient as any)?.foreName}{" "}
                                  {(patient as any)?.surName}
                                </div>
                                <div className="mt-1 text-sm text-gray-600">
                                  <div>
                                    NHS: {formatNHSNumber(patient?.nhsNumber)}
                                  </div>
                                  <div>
                                    DOB: {formatDOB((patient as any)?.dob)} •{" "}
                                    {formatGender((patient as any)?.sex)}
                                  </div>
                                  <div className="mt-1 text-xs text-gray-500">
                                    {(patient as any)?.address}
                                  </div>
                                </div>
                              </div>
                              <ArrowRight className="w-5 h-5 ml-3 text-blue-600" />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              <div className="pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={handleSkipPDSSearch}
                  className="w-full"
                >
                  Skip PDS Search - Add Patient Manually
                </Button>
              </div>
            </div>
          </div>
        )}
        {(editMode || currentStep === "form-entry") && (
          <div
            // className={`flex flex-col flex-1 min-h-0 ${
            //   activeTab !== "personal" ? "overflow-y-auto" : ""
            // }`}
            className={`flex flex-col flex-1 min-h-0 ${
              activeTab === "surgery" ? "overflow-visible" : "overflow-hidden"
            }`}
          >
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="flex flex-col flex-1 min-h-0 "
            >
              <div className="flex-shrink-0 px-6 pt-4 pb-2 border-b border-gray-200">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setActiveTab("personal")}
                    className={`flex items-center gap-2 px-4 py-2 text-xs font-medium transition-all duration-200 relative ${
                      activeTab === "personal"
                        ? isDarkMode
                          ? "text-white"
                          : "text-gray-900"
                        : isDarkMode
                          ? "text-gray-400 hover:text-gray-200"
                          : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Personal
                    {activeTab === "personal" && (
                      <div className="absolute -bottom-2 left-0 right-0 h-0.5 bg-black rounded-full" />
                    )}
                  </button>
                  <button
                    onClick={() => setActiveTab("contact")}
                    className={`flex items-center gap-2 px-4 py-2 text-xs font-medium transition-all duration-200 relative ${
                      activeTab === "contact"
                        ? isDarkMode
                          ? "text-white"
                          : "text-gray-900"
                        : isDarkMode
                          ? "text-gray-400 hover:text-gray-200"
                          : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Contact
                    {activeTab === "contact" && (
                      <div className="absolute -bottom-2 left-0 right-0 h-0.5 bg-black rounded-full" />
                    )}
                  </button>
                  <button
                    onClick={() => setActiveTab("relationships")}
                    className={`flex items-center gap-2 px-4 py-2 text-xs font-medium transition-all duration-200 relative ${
                      activeTab === "relationships"
                        ? isDarkMode
                          ? "text-white"
                          : "text-gray-900"
                        : isDarkMode
                          ? "text-gray-400 hover:text-gray-200"
                          : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Relationships
                    {activeTab === "relationships" && (
                      <div className="absolute -bottom-2 left-0 right-0 h-0.5 bg-black rounded-full" />
                    )}
                  </button>
                  <button
                    onClick={() => setActiveTab("medical")}
                    className={`flex items-center gap-2 px-4 py-2 text-xs font-medium transition-all duration-200 relative ${
                      activeTab === "medical"
                        ? isDarkMode
                          ? "text-white"
                          : "text-gray-900"
                        : isDarkMode
                          ? "text-gray-400 hover:text-gray-200"
                          : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Medical
                    {activeTab === "medical" && (
                      <div className="absolute -bottom-2 left-0 right-0 h-0.5 bg-black rounded-full" />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab("surgery");
                      const surgeryId =
                        selectedSurgery?._id ||
                        selectedSurgery?.id ||
                        formData?.surgeryData?._id ||
                        formData?.surgeryData?.id;
                      if (editMode && surgeryId) {
                        getSurgeryById(surgeryId);
                      }
                    }}
                    className={`flex items-center gap-2 px-4 py-2 text-xs font-medium transition-all duration-200 relative ${
                      activeTab === "surgery"
                        ? isDarkMode
                          ? "text-white"
                          : "text-gray-900"
                        : isDarkMode
                          ? "text-gray-400 hover:text-gray-200"
                          : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Surgery
                    {activeTab === "surgery" && (
                      <div className="absolute -bottom-2 left-0 right-0 h-0.5 bg-black rounded-full" />
                    )}
                  </button>
                </div>
              </div>
              <div
                className={`relative flex-1 min-h-0 add-patient-modal ${
                  activeTab === "surgery"
                    ? "overflow-visible"
                    : "overflow-hidden"
                }`}
              >
                <TabsContent
                  value="personal"
                  className="p-6 m-0 space-y-4 focus-visible:ring-0 overflow-y-auto h-full custom-scrollbar"
                >
                  <div className="space-y-4">
                    <div
                      className={`flex items-center gap-2 pb-2 border-b transition-colors duration-300 ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      }`}
                    >
                      <User className="w-5 h-5 text-blue-600" />
                      <h3
                        className={`text-lg font-semibold transition-colors duration-300 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Personal Details
                      </h3>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label
                          htmlFor="title"
                          className={`transition-colors duration-300 ${
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }`}
                        >
                          Title
                        </Label>
                        <select
                          id="title"
                          value={formData.title}
                          onChange={(e) =>
                            handleInputChangeWrapper("title", e.target.value)
                          }
                          className={`mt-1 text-sm w-full h-10 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-300 ${
                            isDarkMode
                              ? "bg-gray-800 border border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                        >
                          <option
                            className={
                              isDarkMode
                                ? "bg-gray-800 text-white"
                                : "bg-white text-gray-900"
                            }
                            value=""
                          >
                            Select title
                          </option>
                          {titleOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label
                          htmlFor="givenName"
                          className={`transition-colors duration-300 ${
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }`}
                        >
                          Given Name *
                        </Label>
                        <Input
                          id="givenName"
                          value={formData.givenName}
                          onChange={(e) => {
                            const val = e.target.value;
                            setGivenNameLocal(val);

                            // Update the canonical full name in formData (keeps payload logic unchanged)
                            // const other = familyNameLocal
                            //   ? ` ${familyNameLocal}`
                            //   : "";
                            // handleInputChangeWrapper(
                            //   "name",
                            //   `${val}${other}`.trim()
                            // );
                            handleInputChangeWrapper("givenName", val);
                          }}
                          placeholder="Enter given name"
                          className={`mt-1 ${
                            isDarkMode
                              ? "bg-gray-800 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                        />
                        {/* show name error (same as before) under given name for visibility */}
                        {errors?.givenName && (
                          <p className="mt-2 text-sm text-red-500">
                            {String(errors?.givenName)}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label
                          htmlFor="familyName"
                          className={`transition-colors duration-300 ${
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }`}
                        >
                          Family Name *
                        </Label>
                        <Input
                          id="familyName"
                          value={formData.familyName}
                          onChange={(e) => {
                            const val = e.target.value;
                            setFamilyNameLocal(val);

                            // Update only family name in formData, not the combined name
                            handleInputChangeWrapper("familyName", val);
                          }}
                          placeholder="Enter family name"
                          className={`mt-1 ${
                            isDarkMode
                              ? "bg-gray-800 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                        />
                        {errors?.familyName && (
                          <p className="mt-2 text-sm text-red-500">
                            {String(errors?.familyName)}
                          </p>
                        )}
                        {/* If you want an explicit error under family name instead of given name, you could duplicate/route errors here. */}
                      </div>

                      <div>
                        <Label
                          htmlFor="dob"
                          className={`transition-colors duration-300 ${
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }`}
                        >
                          Date of Birth *
                        </Label>
                        <Input
                          id="dob"
                          type="date"
                          value={formData.dateOfBirth}
                          onChange={(e) =>
                            handleInputChangeWrapper(
                              "dateOfBirth",
                              e.target.value,
                            )
                          }
                          max={new Date().toISOString().split("T")[0]}
                          className={`mt-1 ${
                            isDarkMode
                              ? "bg-gray-800 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                        />
                        {errors.dateOfBirth && (
                          <p className="mt-2 text-sm text-red-500">
                            {String(errors.dateOfBirth)}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label
                          htmlFor="gender"
                          className={`transition-colors duration-300 ${
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }`}
                        >
                          Gender
                        </Label>
                        <select
                          id="gender"
                          value={formData.gender}
                          onChange={(e) =>
                            handleInputChangeWrapper("gender", e.target.value)
                          }
                          className={`mt-1 text-sm w-full h-10 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-300 ${
                            isDarkMode
                              ? "bg-gray-800 border border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                        >
                          <option
                            className={
                              isDarkMode
                                ? "bg-gray-800 text-white"
                                : "bg-white text-gray-900"
                            }
                            value=""
                          >
                            Select gender
                          </option>
                          {genderOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {errors.gender && (
                          <p className="mt-2 text-sm text-red-500">
                            {String(errors.gender)}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label
                          htmlFor="ethnicity"
                          className={`transition-colors duration-300 ${
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }`}
                        >
                          Ethnicity
                        </Label>
                        <select
                          id="ethnicity"
                          value={formData.ethnicity}
                          onChange={(e) =>
                            handleInputChangeWrapper(
                              "ethnicity",
                              e.target.value,
                            )
                          }
                          className={`mt-1 w-full text-sm h-10 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-300 ${
                            isDarkMode
                              ? "bg-gray-800 border border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                        >
                          <option
                            value=""
                            className={
                              isDarkMode
                                ? "bg-gray-800 text-white"
                                : "bg-white text-gray-900"
                            }
                          >
                            Select ethnicity
                          </option>
                          {ethnicityOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {errors.ethnicity && (
                          <p className="mt-2 text-sm text-red-500">
                            {String(errors.ethnicity)}
                          </p>
                        )}
                      </div>

                      <div className="w-full col-span-2">
                        <Label
                          className={`transition-colors duration-300 ${
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }`}
                          htmlFor="nhs"
                        >
                          NHS Number
                        </Label>
                        <Input
                          id="nhs"
                          value={formData.nhsNumber}
                          onChange={(e) =>
                            handleInputChangeWrapper(
                              "nhsNumber",
                              e.target.value,
                            )
                          }
                          placeholder="Enter NHS number"
                          className={`mt-1 ${
                            isDarkMode
                              ? "bg-gray-800 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                        />
                        {errors.nhsNumber && (
                          <p className="mt-2 text-sm text-red-500">
                            {String(errors.nhsNumber)}
                          </p>
                        )}
                      </div>

                      <div className="col-span-2">
                        <AddressSearchField
                          label="Address"
                          value={formData.address}
                          onChange={(address, details) => {
                            handleInputChangeWrapper("address", address);
                            if (details) {
                              handleInputChangeWrapper("preferredAddress", {
                                houseName:
                                  (details as any).addressLineOne || "",
                                streetName:
                                  (details as any).addressLineTwo || "",
                                locality: "",
                                town: (details as any).postTown || "",
                                postcode: (details as any).postCode || "",
                                county: (details as any).postalCounty || "",
                              });
                            }
                          }}
                          placeholder="Start typing to search for an address..."
                          isDarkMode={isDarkMode}
                          postcodeValue={postcodeValue}
                          setPostcodeValue={setPostcodeValue}
                          addresses={addresses}
                          setAddresses={setAddresses}
                        />
                      </div>

                      {/* Address Fields (2 per row) */}
                      <div>
                        <Label
                          className={
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }
                        >
                          House Number
                        </Label>
                        <Input
                          // value={house}
                          // onChange={(e) => setHouse(e.target.value)}
                          //     value={addressDetails.houseNumber}
                          // onChange={(e) => setAddressDetails(p => ({ ...p, houseNumber: e.target.value }))}
                          value={
                            formData?.preferredAddress != null
                              ? formData?.preferredAddress?.houseName
                              : formData?.address
                          }
                          onChange={(e) =>
                            handleInputChangeWrapper("preferredAddress", {
                              ...formData.preferredAddress,
                              houseName: e.target.value,
                            })
                          }
                          className={`mt-1 ${
                            isDarkMode
                              ? "bg-gray-800 border-gray-600 text-white"
                              : "bg-white border-gray-300 text-gray-900"
                          }`}
                        />
                      </div>

                      <div>
                        <Label
                          className={
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }
                        >
                          Postcode
                        </Label>
                        <Input
                          // value={addressDetails.postcode}
                          // onChange={(e) => setAddressDetails(p => ({ ...p, postcode: e.target.value }))}
                          // value={postcode}
                          // onChange={(e) => handlePostcodeChange(e.target.value)}

                          value={formData?.preferredAddress?.postcode || ""}
                          onChange={(e) =>
                            handleInputChangeWrapper("preferredAddress", {
                              ...formData.preferredAddress,
                              postcode: e.target.value,
                            })
                          }
                          className={`mt-1 ${
                            isDarkMode
                              ? "bg-gray-800 border-gray-600 text-white"
                              : "bg-white border-gray-300 text-gray-900"
                          }`}
                        />
                      </div>

                      <div>
                        <Label
                          className={
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }
                        >
                          Street Name
                        </Label>
                        <Input
                          value={formData?.preferredAddress?.streetName || ""}
                          onChange={(e) =>
                            handleInputChangeWrapper("preferredAddress", {
                              ...formData.preferredAddress,
                              streetName: e.target.value,
                            })
                          }
                          className={`mt-1 ${
                            isDarkMode
                              ? "bg-gray-800 border-gray-600 text-white"
                              : "bg-white border-gray-300 text-gray-900"
                          }`}
                        />
                      </div>

                      <div>
                        <Label
                          className={
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }
                        >
                          Locality
                        </Label>
                        <Input
                          //   value={addressDetails.locality}
                          // onChange={(e) => setAddressDetails(p => ({ ...p, locality: e.target.value }))}
                          // value={locality}
                          // onChange={(e) => setLocality(e.target.value)}
                          value={formData?.preferredAddress?.locality || ""}
                          onChange={(e) =>
                            handleInputChangeWrapper("preferredAddress", {
                              ...formData.preferredAddress,
                              locality: e.target.value,
                            })
                          }
                          className={`mt-1 ${
                            isDarkMode
                              ? "bg-gray-800 border-gray-600 text-white"
                              : "bg-white border-gray-300 text-gray-900"
                          }`}
                        />
                      </div>

                      <div>
                        <Label
                          className={
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }
                        >
                          Town / City
                        </Label>
                        <Input
                          value={formData?.preferredAddress?.town || ""}
                          onChange={(e) =>
                            handleInputChangeWrapper("preferredAddress", {
                              ...formData.preferredAddress,
                              town: e.target.value,
                            })
                          }
                          className={`mt-1 ${
                            isDarkMode
                              ? "bg-gray-800 border-gray-600 text-white"
                              : "bg-white border-gray-300 text-gray-900"
                          }`}
                        />
                      </div>

                      <div>
                        <Label
                          className={
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }
                        >
                          Country
                        </Label>
                        <Input
                          // value={streetNumber}
                          // onChange={(e) => setStreetNumber(e.target.value)}
                          value={formData?.preferredAddress?.county || ""}
                          onChange={(e) =>
                            handleInputChangeWrapper("preferredAddress", {
                              ...formData.preferredAddress,
                              county: e.target.value,
                            })
                          }
                          className={`mt-1 ${
                            isDarkMode
                              ? "bg-gray-800 border-gray-600 text-white"
                              : "bg-white border-gray-300 text-gray-900"
                          }`}
                        />
                      </div>
                    </div>
                    <div>
                      {errors.address && (
                        <p className="mt-2 text-sm text-red-500 col-span-2">
                          {String(errors.address)}
                        </p>
                      )}
                      <Label
                        className={`transition-colors duration-300 ${
                          isDarkMode ? "text-gray-200" : "text-gray-900"
                        }`}
                        htmlFor="additionalTags"
                      >
                        Additional Tags
                      </Label>

                      <TagsPicker
                        isDarkMode={isDarkMode}
                        isCompanyTagsLoading={isCompanyTagsLoading}
                        tagsOptions={tagsOptions}
                        setTagsOptions={setTagsOptions}
                        toggleTagsPickerDropdown={toggleTagsPickerDropdown}
                        isOpenTagsPickerDropdown={isOpenTagsPickerDropdown}
                        setIsOpenTagsPickerDropdown={
                          setIsOpenTagsPickerDropdown
                        }
                        selectedCompanyTags={selectedCompanyTags}
                        tagFilterValue={tagFilterValue}
                        setTagFilterValue={setTagFilterValue}
                        generateRandomTagsColor={generateRandomTagsColor}
                        previewTagColor={previewTagColor}
                        setPreviewTagColor={setPreviewTagColor}
                        handleSelectTag={handleSelectTag}
                        handleAddCompanyTag={handleAddCompanyTag}
                        currentTag={currentTag}
                        setCurrentTag={setCurrentTag}
                        handleDeleteTag={handleDeleteTag}
                        confirmDelete={confirmDelete}
                        editingTag={editingTag}
                        setEditingTag={setEditingTag}
                      />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent
                  value="contact"
                  className="p-6 m-0 space-y-4 focus-visible:ring-0 overflow-y-auto h-full"
                >
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b">
                      <Phone className="w-5 h-5 text-green-600" />
                      <h3
                        className={`text-lg font-semibold text-gray-900 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Contact Information
                      </h3>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label
                          className={`transition-colors duration-300 ${
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }`}
                          htmlFor="telephone"
                        >
                          Telephone *
                        </Label>
                        <Input
                          id="telephone"
                          value={formData.telephone}
                          onChange={(e) =>
                            handleInputChangeWrapper(
                              "telephone",
                              e.target.value,
                            )
                          }
                          placeholder="Enter phone number"
                          className={`mt-1 ${
                            isDarkMode
                              ? "bg-gray-800 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                        />
                        {errors.telephone && (
                          <p className="mt-2 text-sm text-red-500">
                            {String(errors.telephone)}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label
                          className={`transition-colors duration-300 ${
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }`}
                          htmlFor="email"
                        >
                          Email Address
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) =>
                            handleInputChangeWrapper("email", e.target.value)
                          }
                          placeholder="Enter email address"
                          className={`mt-1 ${
                            isDarkMode
                              ? "bg-gray-800 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                        />
                        {errors.email && (
                          <p className="mt-2 text-sm text-red-500">
                            {String(errors.email)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent
                  value="relationships"
                  className="p-6 m-0 space-y-4 focus-visible:ring-0 overflow-y-auto h-full"
                >
                  <div className="space-y-4">
                    <div
                      className={`flex items-center gap-2 pb-2 border-b transition-colors duration-300 ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      }`}
                    >
                      <UserPlus className="w-5 h-5 text-orange-600" />
                      <h3
                        className={`text-lg font-semibold transition-colors duration-300 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Patient Relationships
                      </h3>
                    </div>

                    <div
                      className={`space-y-4 ${
                        requiresConsent && !hasGuardian
                          ? "p-4 border-2 border-red-200 rounded-lg bg-red-50/30"
                          : ""
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <Label
                          className={`text-sm font-semibold ${
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }`}
                        >
                          Patient Relationships
                        </Label>
                        {requiresConsent && (
                          <div className="flex items-center gap-1 px-2 py-1 text-xs text-orange-600 rounded-md bg-orange-50">
                            <AlertTriangle className="w-3 h-3" />
                            <span>Guardian consent required (under 16)</span>
                          </div>
                        )}
                      </div>

                      {requiresConsent && !hasGuardian && (
                        <div className="p-3 border border-red-200 rounded-md bg-red-50">
                          <div className="flex items-center gap-2 text-red-700">
                            <AlertTriangle className="w-4 h-4" />
                            <span className="text-sm font-medium">
                              Parent or Guardian Required
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-red-600">
                            Patient is {patientAge} years old and requires a
                            parent or guardian relationship for consent purposes
                            whose age should be at least 16 years.
                          </p>
                        </div>
                      )}

                      {/* Search for existing patients */}
                      {!showInlineAddRelated && (
                        <div className="relative" style={{ zIndex: 100 }}>
                          <Label
                            className={`text-sm font-semibold ${
                              isDarkMode ? "text-gray-200" : "text-gray-900"
                            }`}
                            htmlFor="patientSearch"
                          >
                            Search Existing Patient
                          </Label>
                          <Input
                            id="patientSearch"
                            value={patientSearchQuery}
                            onChange={(e) =>
                              setPatientSearchQuery(e.target.value)
                            }
                            placeholder="Search for patients by name, address, or tags..."
                            className={`mt-1 ${
                              isDarkMode
                                ? "bg-gray-800 border-gray-600 text-white placeholder-gray-400"
                                : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                            }`}
                          />
                          {isSearching && (
                            <div
                              className="absolute text-sm text-gray-500 right-2 top-8"
                              style={{ zIndex: 101 }}
                            >
                              Searching...
                            </div>
                          )}

                          {showSearchResults && searchResults.length > 0 && (
                            <div
                              className="absolute w-full mt-1 overflow-y-auto bg-white border border-gray-300 rounded-md shadow-lg max-h-48"
                              style={{
                                zIndex: 999,
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                right: 0,
                              }}
                            >
                              {searchResults.map((patient) => (
                                <div
                                  key={patient._id}
                                  className="p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50"
                                  onClick={() => {
                                    if (patient.isUnder16) return;
                                    setSelectedPatient(patient);
                                    setShowSearchResults(false);
                                  }}
                                >
                                  <div className="flex justify-between">
                                    <div className="font-medium">
                                      {patient.patientName || ""}
                                    </div>
                                    {patient?.isUnder16 && (
                                      <div className="flex items-center gap-1 px-2 py-1 text-xs text-orange-600 rounded-md bg-orange-50">
                                        <AlertTriangle className="w-3 h-3" />
                                        <span>
                                          Cannot be selected as guardian (under
                                          16)
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {`${patient?.dob} (${patient?.age})`} •{" "}
                                    {patient?.nhsNumber || ""}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {patient.address || ""}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {showSearchResults &&
                            searchResults.length === 0 &&
                            patientSearchQuery.length >= 3 &&
                            !isSearching && (
                              <div
                                className="absolute w-full p-3 mt-1 bg-white border border-gray-300 rounded-md shadow-lg"
                                style={{
                                  zIndex: 999,
                                  top: "100%",
                                  left: 0,
                                  right: 0,
                                }}
                              >
                                <div className="text-sm text-gray-500">
                                  No patients found
                                </div>
                              </div>
                            )}
                        </div>
                      )}

                      {/* Add New Related Patient button */}
                      {!showInlineAddRelated && !selectedPatient && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowInlineAddRelated(true);
                            setShowSearchResults(false);
                            setPatientSearchQuery("");
                          }}
                          className="w-full"
                        >
                          <UserPlus className="w-4 h-4 mr-2" />
                          Add New Related Patient
                        </Button>
                      )}

                      {/* Inline Add New Related Patient Form */}
                      {showInlineAddRelated && (
                        <InlineAddRelatedPatient
                          isDarkMode={isDarkMode}
                          requiresConsent={requiresConsent}
                          hasGuardian={hasGuardian}
                          relationshipOptions={relationshipOptions}
                          onCancel={() => setShowInlineAddRelated(false)}
                          onSave={async (newPatientData, relationshipType) => {
                            try {
                              const created =
                                await handleAddNewRelatedPatient(
                                  newPatientData,
                                );
                              addRelationship(
                                {
                                  id: created.patientID,
                                  name: `${created.foreName} ${created.surName}`.trim(),
                                  phone: created.contacts?.[0]?.value || "",
                                  email: created.email || "",
                                  address: created.address || "",
                                },
                                relationshipType,
                                formData,
                                setFormData,
                              );
                              setShowInlineAddRelated(false);
                            } catch (err: any) {
                              alert(
                                err?.message || "Failed to add related patient",
                              );
                            }
                          }}
                        />
                      )}

                      {/* Selected existing patient — relationship type picker */}
                      {selectedPatient && !showInlineAddRelated && (
                        <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                          <div className="space-y-3">
                            <div>
                              <div className="font-medium">
                                Selected Patient: {selectedPatient.patientName}
                              </div>
                              <div className="text-sm text-gray-600">
                                {`${selectedPatient.dob} (${selectedPatient.age})`}
                                {" • "}
                                {selectedPatient.nhsNumber}
                              </div>
                            </div>

                            <div>
                              <Label htmlFor="relationshipTypeSelect">
                                Relationship Type
                                {requiresConsent && !hasGuardian && (
                                  <span className="ml-2 text-xs text-red-600">
                                    (Parent or Guardian required)
                                  </span>
                                )}
                              </Label>
                              <Select
                                value={selectedRelationshipType || undefined}
                                onValueChange={setSelectedRelationshipType}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue placeholder="Select relationship type" />
                                </SelectTrigger>
                                <SelectContent>
                                  {relationshipOptions.map((option) => (
                                    <SelectItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="flex gap-2">
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => {
                                  if (selectedRelationshipType) {
                                    addRelationship(
                                      {
                                        id: selectedPatient._id,
                                        name: selectedPatient.patientName,
                                        phone:
                                          selectedPatient.contacts !== null
                                            ? selectedPatient.contacts[0]?.phone
                                            : "",
                                        email: selectedPatient.email,
                                        address: selectedPatient.address || "",
                                      },
                                      selectedRelationshipType,
                                      formData,
                                      setFormData,
                                    );
                                    setTimeout(() => {
                                      setSelectedPatient(null);
                                      setSelectedRelationshipType("");
                                      setPatientSearchQuery("");
                                    }, 0);
                                  }
                                }}
                                disabled={!selectedRelationshipType}
                              >
                                Add Relationship
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedPatient(null);
                                  setSelectedRelationshipType("");
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Current relationships list */}
                      <div className="space-y-2">
                        {formData.relationships.length === 0 ? (
                          <div className="py-6 text-center text-gray-500 border border-gray-200 rounded-xl bg-gray-50">
                            <UserPlus className="w-6 h-6 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No relationships added</p>
                            <p className="mt-1 text-xs">
                              Search for patients or add a new one
                            </p>
                          </div>
                        ) : (
                          formData.relationships.map((relationship) => (
                            <div
                              key={relationship.id}
                              className="flex items-start justify-between p-4 bg-white border rounded-lg"
                            >
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <div className="font-medium text-gray-900">
                                    {relationship.patientName}
                                  </div>
                                  <div className="px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-md">
                                    {relationshipOptions.find(
                                      (opt) =>
                                        Number(opt.value) ===
                                        Number(relationship.relationshipType),
                                    )?.label || ""}
                                  </div>
                                </div>
                                <div className="space-y-1 text-sm text-gray-600">
                                  {relationship.patientPhone && (
                                    <div className="flex items-center gap-2">
                                      <Phone className="w-3 h-3" />
                                      <span>{relationship.patientPhone}</span>
                                    </div>
                                  )}
                                  {relationship.patientEmail && (
                                    <div className="flex items-center gap-2">
                                      <Mail className="w-3 h-3" />
                                      <span>{relationship.patientEmail}</span>
                                    </div>
                                  )}
                                  {relationship.patientAddress && (
                                    <div className="flex items-center gap-2">
                                      <MapPin className="w-3 h-3" />
                                      <span className="text-xs">
                                        {relationship.patientAddress}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  removeRelationship(
                                    relationship.id,
                                    formData,
                                    setFormData,
                                  )
                                }
                                className="flex-shrink-0 ml-3"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Medical Information Tab */}
                <TabsContent
                  value="medical"
                  className="p-6 m-0 space-y-6 focus-visible:ring-0"
                >
                  <div className="space-y-6">
                    <div className="flex items-center gap-2 pb-2 border-b">
                      <Heart className="w-5 h-5 text-red-600" />
                      <h3 className="text-lg font-semibold text-gray-900">
                        Medical Information
                      </h3>
                    </div>

                    {/* Allergies */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-semibold">
                          Allergies
                        </Label>
                        <AddAllergyButton onSave={handleSaveAllergyWrapper} />
                      </div>

                      <div className="space-y-2">
                        {formData.allergies.length === 0 ? (
                          <div className="py-6 text-center text-gray-500 border border-gray-200 rounded-xl bg-gray-50">
                            <Heart className="w-6 h-6 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No allergies added</p>
                            <p className="mt-1 text-xs">
                              Click the + button to add allergies
                            </p>
                          </div>
                        ) : (
                          formData.allergies.map((allergy) => (
                            <div
                              key={allergy.id}
                              className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl group hover:bg-gray-50"
                            >
                              <Heart className="w-4 h-4 text-gray-900" />
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">
                                  {allergy.allergyName}
                                </div>
                                {allergy.typicalReaction && (
                                  <div className="text-xs text-gray-500">
                                    {allergy.typicalReaction}
                                  </div>
                                )}
                              </div>

                              <div
                                className={`text-xs px-2 py-1 rounded-full capitalize ${getSeverityColorClass(
                                  typeof allergy.severity === "number"
                                    ? severityNumberToString[allergy.severity]
                                    : allergy.severity,
                                )}`}
                              >
                                {typeof allergy.severity === "number"
                                  ? severityNumberToString[allergy.severity]
                                  : allergy.severity}
                              </div>
                              <div className="flex items-center gap-1 transition-opacity opacity-0 group-hover:opacity-100">
                                <AddAllergyButton
                                  editMode={{
                                    id: allergy.id,
                                    allergyName: allergy.allergyName,
                                    severity: allergy.severity,
                                    reaction: allergy.typicalReaction,
                                    notes: allergy.additionalNotes,
                                  }}
                                  onSave={(data) =>
                                    handleEditAllergyWrapper(allergy.id, data)
                                  }
                                  trigger={
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="w-8 h-8 p-0"
                                    >
                                      <Edit2 className="w-3 h-3 text-gray-500" />
                                    </Button>
                                  }
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="w-8 h-8 p-0"
                                  onClick={() => {
                                    confirmDelete(
                                      () =>
                                        handleDeleteAllergyWrapper(
                                          allergy.allergyName,
                                        ),
                                      deleteConfirmationMessage + "allergy",
                                    );
                                  }}
                                >
                                  <Trash2 className="w-3 h-3 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Medical Conditions */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-semibold">
                          Medical Conditions
                        </Label>
                        <AddConditionButton
                          onSave={handleSaveConditionWrapper}
                        />
                      </div>

                      <div className="space-y-2">
                        {formData.conditions.length === 0 ? (
                          <div className="py-6 text-center text-gray-500 border border-gray-200 rounded-xl bg-gray-50">
                            <FileText className="w-6 h-6 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">
                              No medical conditions added
                            </p>
                            <p className="mt-1 text-xs">
                              Click the + button to add conditions
                            </p>
                          </div>
                        ) : (
                          formData.conditions.map((condition) => (
                            <div
                              key={condition.id}
                              className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl group hover:bg-gray-50"
                            >
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">
                                  {condition.conditionName}
                                </div>
                                {condition.additionalNotes && (
                                  <div className="text-xs text-gray-500">
                                    {condition.additionalNotes}
                                  </div>
                                )}
                                {condition.diagnosedDate && (
                                  <div className="text-xs text-gray-400">
                                    Diagnosed:{" "}
                                    {new Date(
                                      condition.diagnosedDate,
                                    ).toLocaleDateString()}
                                  </div>
                                )}
                              </div>

                              <div
                                className={`text-xs px-2 py-1 rounded-full capitalize ${getStatusColorClass(
                                  typeof condition.status === "number"
                                    ? statusNumberToString(condition.status)
                                    : condition.status,
                                )}`}
                              >
                                {typeof condition.status === "number"
                                  ? statusNumberToString(condition.status)
                                  : condition.status}
                              </div>
                              <div className="flex items-center gap-1 transition-opacity opacity-0 group-hover:opacity-100">
                                <AddConditionButton
                                  editMode={{
                                    id: condition.id,
                                    conditionName: condition.conditionName,
                                    status: condition.status,
                                    diagnosedDate: condition.diagnosedDate,
                                    notes: condition.additionalNotes,
                                  }}
                                  onSave={(data) =>
                                    handleEditConditionWrapper(
                                      condition.id,
                                      data,
                                    )
                                  }
                                  trigger={
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="w-8 h-8 p-0"
                                    >
                                      <Edit2 className="w-3 h-3 text-gray-500" />
                                    </Button>
                                  }
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="w-8 h-8 p-0"
                                  onClick={() => {
                                    confirmDelete(
                                      () =>
                                        handleDeleteConditionWrapper(
                                          condition.conditionName,
                                        ),
                                      deleteConfirmationMessage + "condition",
                                    );
                                  }}
                                >
                                  <Trash2 className="w-3 h-3 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Medications */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-semibold">
                          Current Medications
                        </Label>
                        <AddMedicationButton
                          onSave={handleSaveMedicationWrapper}
                        />
                      </div>

                      <div className="space-y-2">
                        {formData.medications.length === 0 ? (
                          <div className="py-6 text-center text-gray-500 border border-gray-200 rounded-xl bg-gray-50">
                            <Pill className="w-6 h-6 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No medications added</p>
                            <p className="mt-1 text-xs">
                              Click the + button to add medications
                            </p>
                          </div>
                        ) : (
                          formData.medications.map((medication) => (
                            <div
                              key={medication.id}
                              className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl group hover:bg-gray-50"
                            >
                              <Pill className="w-4 h-4 text-gray-900" />
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">
                                  {medication.itemName}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {medication.dose}
                                </div>
                              </div>

                              <div
                                className={`text-xs px-2 py-1 rounded-full capitalize ${getTherapyLevelColorClass(
                                  typeof medication.therapyLevel === "number"
                                    ? therapyLevelNumberToString[
                                        medication.therapyLevel
                                      ]
                                    : medication.therapyLevel,
                                )}`}
                              >
                                {typeof medication.therapyLevel === "number"
                                  ? therapyLevelNumberToString[
                                      medication.therapyLevel
                                    ]
                                  : medication.therapyLevel}
                              </div>
                              <div className="flex items-center gap-1 transition-opacity opacity-0 group-hover:opacity-100">
                                <AddMedicationButton
                                  editMode={{
                                    id: medication.id,
                                    item: medication.itemName,
                                    dose: medication.dose,
                                    times: medication.times,
                                    quantity: medication.quantity,
                                    therapyLevel: medication.therapyLevel,
                                    preparation: medication.preparation,
                                    notes: medication.additionalNotes,
                                  }}
                                  onSave={(data) =>
                                    handleEditMedicationWrapper(
                                      medication.id,
                                      data,
                                    )
                                  }
                                  trigger={
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="w-8 h-8 p-0"
                                    >
                                      <Edit2 className="w-3 h-3 text-gray-500" />
                                    </Button>
                                  }
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="w-8 h-8 p-0"
                                  onClick={() => {
                                    confirmDelete(
                                      () =>
                                        handleDeleteMedicationWrapper(
                                          medication.itemName,
                                        ),
                                      deleteConfirmationMessage + "medication",
                                    );
                                  }}
                                >
                                  <Trash2 className="w-3 h-3 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Surgery Information Tab */}
                <TabsContent
                  value="surgery"
                  className="px-6 py-6 pb-40 m-0 space-y-4 focus-visible:ring-0 overflow-y-auto h-full custom-scrollbar"
                >
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b">
                      <Stethoscope className="w-5 h-5 text-purple-600" />
                      <h3 className="text-lg font-semibold text-gray-900">
                        Surgery Information
                      </h3>
                    </div>

                    {/* Step 1: Surgery Search */}
                    <div className="space-y-3">
                      <Label htmlFor="surgery-search">Search for Surgery</Label>
                      <div data-surgery-container>
                        <Input
                          ref={surgeryInputRef}
                          id="surgery-search"
                          value={surgerySearchQuery}
                          onChange={async (e) => {
                            setSurgerySearchQuery(e.target.value);
                            if (e.target.value.trim().length < 3) {
                              if (editMode) {
                                setSurgery(null);
                              }
                              setSelectedSurgery(null);
                              lastSelectedSurgeryKeyRef.current = "";
                              setFormData((prev: any) => ({
                                ...prev,
                                surgeryName: "",
                                surgeryAddress: "",
                                surgeryPhone: "",
                                surgeryEmail: "",
                                doctorName: "",
                                surgeryData: null,
                                gpData: null,
                              }));
                              setGpSearchQuery("");
                              setIsManualGpEntry(false);
                              setShowGpResults(false);
                              setShowEmailResults(false);
                              setShowSurgeryResults(false);
                            } else {
                              await searchSurgery({ Keyword: e.target.value });
                              setShowSurgeryResults(true);
                            }
                          }}
                          placeholder="Search by surgery name, address, or postcode..."
                          className="mt-1"
                          disabled={!!selectedSurgery}
                        />

                        {/* Surgery Search Results */}
                        {showSurgeryResults &&
                          searchedSurgeries?.length > 0 && (
                            <div
                              data-surgery-dropdown
                              className="mt-1 bg-white border border-gray-300 rounded-md shadow-md max-h-64 overflow-y-auto"
                            >
                              {searchedSurgeries?.map((surgery) => (
                                <div
                                  key={surgery?.id}
                                  className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                  onClick={() => {
                                    const surgeryEmailOptions =
                                      getSurgeryEmailOptions(surgery);
                                    const surgeryPhoneValue = Array.isArray(
                                      surgery?.contactTelephoneNumber,
                                    )
                                      ? surgery?.contactTelephoneNumber?.[0] ||
                                        ""
                                      : surgery?.contactTelephoneNumber || "";
                                    const surgeryEmailValue =
                                      surgeryEmailOptions[0] || "";

                                    setSelectedSurgery(surgery);
                                    setSurgerySearchQuery(surgery?.name);
                                    setShowSurgeryResults(false);
                                    setShowEmailResults(false);
                                    setFormData((prev: any) => ({
                                      ...prev,
                                      surgeryName: surgery?.name,
                                      surgeryAddress: `${surgery?.addressLine1}, ${surgery?.postcode}`,
                                      surgeryPhone: surgeryPhoneValue,
                                      surgeryEmail: surgeryEmailValue,
                                      surgeryData: surgery,
                                    }));
                                    // Reset GP selection
                                    setGpSearchQuery("");
                                    setIsManualGpEntry(false);
                                  }}
                                >
                                  <div className="font-medium text-gray-900">
                                    {surgery?.name}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    {surgery?.address}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {surgery?.postcode} •{" "}
                                    {surgery?.generalPractitioners?.length} GPs
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                        {showSurgeryResults &&
                          searchedSurgeries?.length === 0 &&
                          surgerySearchQuery.trim().length >= 3 && (
                            <div
                              data-surgery-dropdown
                              className="mt-1 bg-white border border-gray-300 rounded-md shadow-md p-3"
                            >
                              <div className="text-sm text-gray-500">
                                No surgeries found
                              </div>
                            </div>
                          )}
                      </div>

                      {/* Selected Surgery Display */}
                      {selectedSurgery && (
                        <div className="space-y-3">
                          <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="font-medium text-purple-900">
                                  {selectedSurgery.name}
                                </div>
                                <div className="text-sm text-purple-700">
                                  {selectedSurgery?.addressLine1},{" "}
                                  {selectedSurgery?.postcode}
                                </div>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  if (editMode) {
                                    setSurgery(null);
                                  }
                                  setSelectedSurgery(null);
                                  lastSelectedSurgeryKeyRef.current = "";
                                  setSurgerySearchQuery("");
                                  setGpSearchQuery("");
                                  setIsManualGpEntry(false);
                                  setShowGpResults(false);
                                  setShowEmailResults(false);
                                  setFormData((prev: any) => ({
                                    ...prev,
                                    surgeryName: "",
                                    surgeryAddress: "",
                                    surgeryPhone: "",
                                    surgeryEmail: "",
                                    doctorName: "",
                                    surgeryData: null,
                                    gpData: null,
                                  }));
                                }}
                                className="text-purple-600 hover:text-purple-800"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>

                          {/* Surgery Contact Details - Editable */}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="surgery-phone">
                                Surgery Phone
                              </Label>
                              <Input
                                id="surgery-phone"
                                type="tel"
                                value={formData.surgeryPhone ?? ""}
                                onChange={(e) =>
                                  handleInputChangeWrapper(
                                    "surgeryPhone",
                                    e.target.value,
                                  )
                                }
                                placeholder="Enter surgery phone number"
                                className="mt-1"
                              />
                            </div>

                            <div>
                              <Label htmlFor="surgery-email">
                                Surgery Email
                              </Label>
                              <div
                                className="relative"
                                data-surgery-email-container
                              >
                                <Input
                                  id="surgery-email"
                                  type="email"
                                  value={formData.surgeryEmail ?? ""}
                                  onChange={(e) => {
                                    handleInputChangeWrapper(
                                      "surgeryEmail",
                                      e.target.value,
                                    );
                                    setShowEmailResults(true);
                                  }}
                                  onFocus={() => {
                                    if (surgeryEmailOptions.length > 0) {
                                      setShowEmailResults(true);
                                    }
                                  }}
                                  placeholder="Search or type surgery email"
                                  className="mt-1"
                                />

                                {showEmailResults &&
                                  (() => {
                                    const currentValue = (
                                      formData.surgeryEmail ?? ""
                                    ).toLowerCase();
                                    const filteredEmails = currentValue
                                      ? surgeryEmailOptions.filter((email) =>
                                          email
                                            .toLowerCase()
                                            .includes(currentValue),
                                        )
                                      : surgeryEmailOptions;

                                    if (filteredEmails.length > 0) {
                                      return (
                                        <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-48 overflow-y-auto z-[9999]">
                                          {filteredEmails.map((email) => (
                                            <div
                                              key={email}
                                              className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 text-sm text-gray-900"
                                              onMouseDown={(e) =>
                                                e.preventDefault()
                                              }
                                              onClick={() => {
                                                handleInputChangeWrapper(
                                                  "surgeryEmail",
                                                  email,
                                                );
                                                setShowEmailResults(false);
                                              }}
                                            >
                                              {email}
                                            </div>
                                          ))}
                                        </div>
                                      );
                                    }

                                    if (
                                      (formData.surgeryEmail ?? "").trim()
                                        .length >= 1
                                    ) {
                                      return (
                                        <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-md">
                                          <div className="text-sm text-gray-500">
                                            No emails found
                                          </div>
                                        </div>
                                      );
                                    }

                                    return null;
                                  })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Step 2: GP Selection (only shown after surgery is selected) */}
                    {selectedSurgery && (
                      <div className="space-y-3 pt-2">
                        <div className="flex items-center justify-between">
                          <Label>Select GP</Label>
                          {/* <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setIsManualGpEntry(!isManualGpEntry);
                                setGpSearchQuery("");
                                if (!isManualGpEntry) {
                                  setFormData((prev: any) => ({
                                    ...prev,
                                    doctorName: "",
                                    gpData: null,
                                  }));
                                }
                              }}
                              className="text-xs"
                            >
                              {isManualGpEntry
                                ? "Search GPs"
                                : "Enter Manually"}
                            </Button> */}
                        </div>

                        <div className="relative" data-gp-container>
                          <Input
                            ref={gpInputRef}
                            value={gpSearchQuery}
                            onChange={(e) => {
                              setGpSearchQuery(e.target.value);
                              setShowGpResults(true);
                            }}
                            onFocus={() => {
                              if (surgeryGpOptions.length > 0) {
                                setShowGpResults(true);
                              }
                            }}
                            placeholder="Search for GP by name..."
                            className="mt-1"
                          />

                          {/* GP Search Results - filtered from selected surgery's GPs */}
                          {showGpResults &&
                            (() => {
                              const filtered = gpSearchQuery.trim()
                                ? surgeryGpOptions.filter((gp) => {
                                    const gpName = String(
                                      gp?.name ??
                                        gp?.Name ??
                                        gp?.fullName ??
                                        "",
                                    ).toLowerCase();
                                    return gpName.includes(
                                      gpSearchQuery.toLowerCase(),
                                    );
                                  })
                                : surgeryGpOptions;
                              if (filtered.length > 0) {
                                return (
                                  <div
                                    data-gp-dropdown
                                    className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-48 overflow-y-auto z-[9999]"
                                  >
                                    {filtered.map((gp: any) => {
                                      const gpName =
                                        gp?.name ??
                                        gp?.Name ??
                                        gp?.fullName ??
                                        "";
                                      return (
                                        <div
                                          key={gp.id ?? gp._id ?? gpName}
                                          className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                          onClick={() => {
                                            setGpSearchQuery(gpName);
                                            setShowGpResults(false);
                                            setFormData((prev: any) => ({
                                              ...prev,
                                              doctorName: gpName,
                                              gpData: gp,
                                            }));
                                          }}
                                        >
                                          <div className="font-medium text-gray-900">
                                            {gpName}
                                          </div>
                                          {gp.specialization && (
                                            <div className="text-xs text-gray-500">
                                              {gp.specialization}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              }
                              if (gpSearchQuery.trim().length >= 1) {
                                return (
                                  <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-md">
                                    <div className="text-sm text-gray-500">
                                      No GPs found
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Doctor Information Tab */}
                {/* <TabsContent
                value="doctor"
                className="p-6 m-0 space-y-4 focus-visible:ring-0"
              >
                <div className="space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b">
                    <Stethoscope className="w-5 h-5 text-purple-600" />
                    <h3 className="text-lg font-semibold text-gray-900">
                      Doctor Information
                    </h3>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="doctor">GP Name</Label>
                      <DoctorSearchableDropdown
                        selectedVal={formData.doctor.gpName}
                        handleChange={(val) =>
                          handleInputChangeWrapper("doctor", {
                            ...formData.doctor,
                            gpName: val,
                          })
                        }
                        isDarkMode={isDarkMode}
                      />
                    </div>

                    <div>
                      <Label htmlFor="surgery">Surgery Name</Label>
                      <Input
                        id="surgery"
                        value={formData.doctor.surgeryName}
                        onChange={(e) =>
                          handleInputChangeWrapper("doctor", {
                            ...formData.doctor,
                            surgeryName: e.target.value,
                          })
                        }
                        placeholder="Enter surgery name"
                        className="mt-1"
                      />
                    </div>

                    <div className="col-span-2">
                      <AddressSearchField
                        label="Surgery Address"
                        value={formData.doctor.surgeryAddress}
                        onChange={(val: string) =>
                          handleInputChangeWrapper("doctor", {
                            ...formData.doctor,
                            surgeryAddress: val,
                          })
                        }
                        placeholder="Start typing to search for surgery address..."
                        isDarkMode={isDarkMode}
                      />
                    </div>
                  </div>
                </div>
              </TabsContent> */}
              </div>
            </Tabs>
          </div>
        )}
        {/* Footer */}
        <div
          className={`flex-shrink-0 flex justify-between items-center pt-6 border-t transition-colors duration-300 ${
            isDarkMode
              ? "bg-gray-900 border-gray-700"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          {addPatientFormMessage === "" ? (
            <div
              className={`text-sm transition-colors duration-300 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              * Required fields
            </div>
          ) : (
            <div
              className={`text-sm transition-colors duration-300 text-red-600 ml-2 italic flex gap-[5px] items-center`}
            >
              <ImWarning size={12} color={"red"} />
              Warning: {addPatientFormMessage}
            </div>
          )}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className={isDarkMode ? "text-gray-200 border-gray-700" : ""}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            {/* <Button
              onClick={handleSubmitWrapper}
              disabled={updatePatientDetailsLoader || addPatientLoader}
              className={`bg-blue-600 hover:bg-blue-700 ${isDarkMode ? "text-white" : ""
                }`}
            >
              {editMode ? (
                <Fragment key="edit-mode">
                  <Edit2 className="w-4 h-4 mr-2" />
                  {updatePatientDetailsLoader
                    ? "Updating..."
                    : "Update Patient"}
                </Fragment>
              ) : (
                <Fragment key="add-mode">
                  <UserPlus className="w-4 h-4 mr-2" />
                  {addPatientLoader ? "Adding..." : "Add Patient"}
                </Fragment>
              )}
            </Button> */}
            <Button
              onClick={handleSubmitWrapper}
              disabled={
                updatePatientDetailsLoader ||
                addPatientLoader ||
                currentStep === "pds-search"
              }
              className={`bg-blue-600 hover:bg-blue-700 ${
                isDarkMode ? "text-white" : ""
              }`}
            >
              {editMode ? (
                <Fragment key="edit-mode">
                  <Edit2 className="w-4 h-4 mr-2" />
                  {updatePatientDetailsLoader
                    ? "Updating..."
                    : "Update Patient"}
                </Fragment>
              ) : (
                <Fragment key="add-mode">
                  <UserPlus className="w-4 h-4 mr-2" />
                  {addPatientLoader ? "Adding..." : "Add Patient"}
                </Fragment>
              )}
            </Button>
          </div>
        </div>

        <DeleteConfirmationModal
          open={deleteModalOpen}
          onOpenChange={setDeleteModalOpen}
          onConfirm={deleteAction}
          title="Delete Confirmation"
          message={deleteMessage}
          isDarkMode={isDarkMode}
        />

        {/* Upload Document Modal */}
        <UploadDocumentModal
          open={uploadDocumentModalOpen}
          onOpenChange={setUploadDocumentModalOpen}
          isDarkMode={isDarkMode}
          onUploadDocument={handleUploadDocument}
          isLoading={false}
        />
      </DialogContent>
    </Dialog>
  );
}

interface InlineAddRelatedPatientProps {
  isDarkMode: boolean;
  requiresConsent: boolean;
  hasGuardian: boolean;
  relationshipOptions: { value: string; label: string }[];
  onCancel: () => void;
  onSave: (
    patientData: {
      foreName: string;
      surName: string;
      dateOfBirth: string;
      email: string;
      telephone: string;
    },
    relationshipType: string,
  ) => Promise<void>;
}

function InlineAddRelatedPatient({
  isDarkMode,
  requiresConsent,
  hasGuardian,
  relationshipOptions,
  onCancel,
  onSave,
}: InlineAddRelatedPatientProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [relType, setRelType] = useState(
    requiresConsent && !hasGuardian ? "2" : "",
  );
  const [foreName, setForeName] = useState("");
  const [surName, setSurName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [email, setEmail] = useState("");
  const [telephone, setTelephone] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const calculateAge = (dob: string): number => {
    if (!dob) return 0;
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!foreName.trim()) errs.foreName = "Forename is required";
    if (!surName.trim()) errs.surName = "Surname is required";
    if (!dateOfBirth) errs.dateOfBirth = "Date of birth is required";
    else if (calculateAge(dateOfBirth) < 16)
      errs.dateOfBirth = "Cannot be added as a related patient (under 16)";
    if (!relType) errs.relType = "Relationship type is required";
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      errs.email = "Enter a valid email";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      await onSave(
        { foreName, surName, dateOfBirth, email, telephone },
        relType,
      );
    } catch {
      // error is handled by parent via alert
    } finally {
      setIsSaving(false);
    }
  };

  const inputClass = `mt-1 ${
    isDarkMode
      ? "bg-gray-800 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  return (
    <div
      className={`rounded-lg border p-4 space-y-3 ${
        isDarkMode
          ? "border-gray-600 bg-gray-800/50"
          : "border-gray-300 bg-gray-50/50"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserPlus className="w-4 h-4 text-blue-600" />
          <span
            className={`text-sm font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Add New Related Patient
          </span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          className="h-7 px-2 text-gray-500"
        >
          ← Back to search
        </Button>
      </div>

      {/* Relationship type */}
      <div>
        <Label
          className={`text-xs font-medium ${
            isDarkMode ? "text-gray-300" : "text-gray-600"
          }`}
        >
          Relationship Type *
          {requiresConsent && !hasGuardian && (
            <span className="ml-2 text-red-500">
              (Parent or Guardian required)
            </span>
          )}
        </Label>
        <Select value={relType || undefined} onValueChange={setRelType}>
          <SelectTrigger className="mt-1 h-9 text-sm">
            <SelectValue placeholder="Select relationship type" />
          </SelectTrigger>
          <SelectContent>
            {relationshipOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.relType && (
          <p className="mt-1 text-xs text-red-500">{errors.relType}</p>
        )}
      </div>

      {/* Name row */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label
            className={`text-xs font-medium ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Forename *
          </Label>
          <Input
            placeholder="Forename"
            value={foreName}
            onChange={(e) => setForeName(e.target.value)}
            className={`h-9 text-sm ${inputClass}`}
          />
          {errors.foreName && (
            <p className="mt-1 text-xs text-red-500">{errors.foreName}</p>
          )}
        </div>
        <div>
          <Label
            className={`text-xs font-medium ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Surname *
          </Label>
          <Input
            placeholder="Surname"
            value={surName}
            onChange={(e) => setSurName(e.target.value)}
            className={`h-9 text-sm ${inputClass}`}
          />
          {errors.surName && (
            <p className="mt-1 text-xs text-red-500">{errors.surName}</p>
          )}
        </div>
      </div>

      {/* DOB */}
      <div>
        <Label
          className={`text-xs font-medium ${
            isDarkMode ? "text-gray-300" : "text-gray-600"
          }`}
        >
          Date of Birth *
        </Label>
        <Input
          type="date"
          value={dateOfBirth}
          onChange={(e) => setDateOfBirth(e.target.value)}
          max={new Date().toISOString().split("T")[0]}
          className={`h-9 text-sm w-full ${inputClass}`}
        />
        {errors.dateOfBirth && (
          <p className="mt-1 text-xs text-red-500">{errors.dateOfBirth}</p>
        )}
      </div>

      {/* Email + Telephone */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label
            className={`text-xs font-medium ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Email
          </Label>
          <Input
            type="email"
            placeholder="name@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`h-9 text-sm ${inputClass}`}
          />
          {errors.email && (
            <p className="mt-1 text-xs text-red-500">{errors.email}</p>
          )}
        </div>
        <div>
          <Label
            className={`text-xs font-medium ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Telephone
          </Label>
          <Input
            type="tel"
            placeholder="07700 900123"
            value={telephone}
            onChange={(e) => setTelephone(e.target.value)}
            className={`h-9 text-sm ${inputClass}`}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
          className="flex-1 h-9"
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={isSaving}
          onClick={handleSave}
          className="flex-1 h-9 bg-blue-600 hover:bg-blue-700 text-white"
        >
          {isSaving ? "Saving..." : "Add & Link Patient"}
        </Button>
      </div>
    </div>
  );
}

export default AddPatientPopUp;
